<template>
  <d-container fluid class="main-content-container px-4">
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('marketing.salesTools') }}</span>
        <h3 class="page-title d-flex align-items-center">
          {{ $t('marketing.editEmailMessage') }}
        </h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end flex-wrap"
        style="gap: 8px">
        <d-row>
          <d-col
            col
            cols="12"
            class="text-right text-sm-right d-flex justify-content-end align-items-end flex-wrap mb-2"
            style="gap: 8px">
            <d-button
              theme="secondary"
              @click.prevent="$router.push('/funkcje-sprzedazowe/wiadomosci-email')">
              <i class="material-icons">arrow_back</i>
              {{ $t('ui.backToList') }}
            </d-button>

            <d-button
              theme="success"
              outline
              :disabled="!can_update"
              @click.prevent="validateForm(true)">
              <i class="material-icons">save</i>
              {{ $t('ui.save') }}
            </d-button>

            <d-button
              theme="success"
              :disabled="!can_update"
              @click.prevent="validateForm(false)">
              <i class="material-icons">save</i>
              {{ $t('marketing.saveAndClose') }}
            </d-button>
          </d-col>
          <d-col
            col
            cols="12"
            class="text-right text-sm-right d-flex justify-content-end align-items-end flex-wrap"
            style="gap: 8px">
            <d-button
              theme="info"
              outline
              @click.prevent="openTestPopup">
              <i class="material-icons">mail_lock</i>
              {{ $t('marketing.sendTestEmail') }}
            </d-button>

            <d-button
              theme="primary"
              :disabled="!can_send"
              @click.prevent="sendMailing">
              <i class="material-icons">send</i>
              {{ $t('marketing.sendMailing') }}
            </d-button>
          </d-col>
        </d-row>
      </d-col>
    </d-row>

    <d-alert
      v-if="loaded && !can_update"
      show
      theme="warning"
      class="mb-3">
      {{ $t('marketing.mailingCannotBeUpdated') }}
    </d-alert>

    <d-alert
      v-if="loaded && !can_send"
      show
      theme="info"
      class="mb-3">
      {{ $t('marketing.mailingCannotBeSent') }}
    </d-alert>

    <!-- Form -->
    <d-form :class="{ 'is-loading': !loaded }">
      <validation-observer
        ref="mailing-edit-form"
        tag="form"
        v-slot="{
          errors: formErrors,
          valid: isFormValid,
          validated: isFormValidated
        }">
        <d-card class="mb-5">
          <d-tabs
            v-model="activeTab"
            v-sticky-tabs
            card
            :nav-class="loadedSegments ? validationResults : ''">

            <!-- Dane ogólne -->
            <d-tab
              :title="$t('ui.generalInfo')"
              active>
              <div
                class="mb-3 pb-3 border-bottom d-flex"
                style="gap: 8ox;">
                <h4 class="my-0">
                  {{ $t('ui.generalInfo') }}
                </h4>
                <mailing-status-badge
                  v-if="loaded && status"
                  :status="status"
                  class="ml-auto my-auto" />
              </div>

              <d-row>
                <d-col cols="12">
                  <div class="form-group">
                    <label for="name">
                      {{ $t('ui.name') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="name"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="name" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
                <d-col cols="12">
                  <div class="form-group">
                    <label for="subject">
                      {{ $t('marketing.emailSubject') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="subject"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="subject" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
                <d-col cols="12">
                  <div class="form-group">
                    <label>
                      {{ $t('marketing.messageContent') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="content"
                      v-slot="{ errors }">

                      <div :class="{ 'editor-is-invalid': errors.length }">
                        <wysiwyg-editor
                          v-model="content"
                          key="tinymce-editor"
                          :placeholder="$t('marketing.insertMessageContent')" />
                      </div>

                      <small class="form-text text-muted mt-2">
                        <i class="material-icons mr-1" style="font-size: 14px; vertical-align: middle; top: unset;">info</i>
                        {{ $t('marketing.availableVariablesInfo') }}:
                        <code class="px-1 bg-light border rounded mx-1">[customer_name]</code>
                        <code class="px-1 bg-light border rounded mx-1">[customer_email]</code>
                      </small>

                      <div v-if="errors.length" class="invalid-feedback d-block">
                        {{ errors[0] }}
                      </div>

                    </validation-provider>
                  </div>
                </d-col>
              </d-row>
            </d-tab>
            <!-- Segmenty -->
            <d-tab :title="$t('marketing.segments')">
              <h4 class="mb-3 pb-3 border-bottom">
                {{ $t('marketing.segments') }}
              </h4>
              <d-alert
                v-if="loadError"
                show
                theme="warning">
                {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
              </d-alert>
              <div
                v-else-if="!loadedSegments"
                class="pb-3 pt-3 text-center overlay-loading">
                {{ $t('ui.dataLoading') }}&hellip;
              </div>
              <validation-provider
                v-else
                rules="required"
                name="segments"
                v-slot="{ errors }">

                <div :class="{ 'table-is-invalid': errors.length }">
                  <mailing-segments-table
                    v-model="segments"
                    :segments="segmentsList" />
                </div>

                <div
                  v-if="errors.length"
                  class="invalid-feedback d-block mt-2">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </d-tab>
            <d-tab :title="$t('marketing.translations')">
              <h4 class="pb-3 mb-0 border-bottom d-flex">
                {{ $t('marketing.translations') }}

                <d-button
                  class="ml-auto"
                  theme="success"
                  @click.prevent="addTranslation"
                  :disabled="!can_update">
                  <i class="material-icons">add</i>
                  {{ $t('marketing.addTranslation') }}
                </d-button>
              </h4>

              <table class="table mt-2 mb-0 full-width">
                <thead class="bg-light">
                  <tr>
                    <th scope="col" class="border-0 align-middle" style="width: 150px;">{{ $t('ui.language') }}</th>
                    <th scope="col" class="border-0 align-middle">{{ $t('marketing.subject') }}</th>
                    <th scope="col" class="border-0 align-middle" style="max-width: 250px;">{{ $t('marketing.messageContent') }}</th>
                    <th scope="col" class="border-0 align-middle text-center" style="width: 130px;">{{ $t('ui.actions') }}</th>
                  </tr>
                </thead>
                <tbody v-if="translations.length">
                  <tr
                    v-for="(translation, index) of translations"
                    :key="'price-' + index">
                    <td class="align-middle">
                      {{ getLanguageByID(translation.language_id) }}
                    </td>
                    <td class="align-middle">
                      {{ translation.subject }}
                    </td>
                    <td
                      class="align-middle text-truncate"
                      style="max-width: 250px;"
                      :title="stripHtml(translation.content)">
                      {{ stripHtml(translation.content) }}
                    </td>
                    <td class="align-middle text-center">
                      <d-button
                        theme="primary"
                        @click.prevent="editTranslation(translation)"
                        class="mr-2"
                        :title="$t('ui.edit')"
                        :disabled="!can_update">
                        <i class="material-icons">edit</i>
                      </d-button>
                      <d-button
                        theme="danger"
                        :disabled="removeTranslationInProgress || !can_update"
                        @click.prevent="removeTranslation(translation.id)"
                        :title="$t('ui.delete')">
                        <i class="material-icons">delete</i>
                      </d-button>
                    </td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td colspan="4" class="text-center">
                      {{ $t('settings.transaltion.noTranslationsToDisplay') }}&hellip;
                    </td>
                  </tr>
                </tbody>
              </table>
            </d-tab>
            <d-tab
              :title="$t('marketing.sentEmails')"
              @active="emailsTabLoaded = true">
              <mailing-emails-list
                v-if="emailsTabLoaded"
                :languages="languages"
                :status="status"
                :template-id="id" />
            </d-tab>
          </d-tabs>
        </d-card>
      </validation-observer>
    </d-form>

    <mailing-translation-new-popup
      v-if="showAddTranslationPopup"
      :template-id="this.id"
      :languages="languages" />

    <mailing-translation-edit-popup
      v-if="showEditTranslationPopup"
      :template-id="this.id"
      :translationData="selectedTranslation"
      :languages="languages" />

    <mailing-test-popup
      v-if="showTestPopup"
      :subject="subject"
      :content="content" />
  </d-container>
</template>

<script>
import Vue from 'vue';
import FormUtils from '@/utils/FormUtils.js';
import ListUtils from '@/utils/ListUtils.js';
import MailingSegmentsTable from './MailingSegmentsTable.vue';
import OrderWysiwyg from '@/components/orders/OrderWysiwyg.vue';
import MailingTranslationNewPopup from './MailingTranslationNewPopup.vue';
import MailingTranslationEditPopup from './MailingTranslationEditPopup.vue';
import MailingTestPopup from './MailingTestPopup.vue';
import MailingStatusBadge from './MailingStatusBadge.vue';
import MailingEmailsList from './MailingEmailsList.vue';
import DOMPurify from 'dompurify';

export default {
  name: 'marketing-mailing-edit',
  components: {
    'wysiwyg-editor': OrderWysiwyg,
    'mailing-segments-table': MailingSegmentsTable,
    'mailing-translation-new-popup': MailingTranslationNewPopup,
    'mailing-translation-edit-popup': MailingTranslationEditPopup,
    'mailing-test-popup': MailingTestPopup,
    'mailing-status-badge': MailingStatusBadge,
    'mailing-emails-list': MailingEmailsList
  },
  computed: {
    validationResults () {
      if (!this.$refs['mailing-edit-form']) {
        return '';
      }

      const errors = this.$refs['mailing-edit-form'].errors;
      const errorKeys = Object.keys(errors).filter(key => errors[key].length > 0);
      const errorKeysString = errorKeys.join(',');
      const errorTabs = [];

      const mapping = [
        ['error-1', ['name', 'subject', 'content']],
        ['error-2', ['segments']]
      ];

      mapping.forEach(([className, fields]) => {
        if (fields.some(field => errorKeysString.includes(field))) {
          errorTabs.push(className);
        }
      });

      return errorTabs.join(' ');
    }
  },
  watch: {
    activeTab (newTabIndex) {
      if (newTabIndex === 3) {
        this.emailsTabLoaded = true;
      }
    }
  },
  data () {
    return {
      id: parseInt(this.$route.params.id, 10),
      content: '',
      name: null,
      segments: [],
      subject: null,
      status: null,
      can_update: false,
      can_send: false,
      loaded: false,
      loadError: false,
      loadedSegments: false,
      segmentsList: [],
      selectAllSegments: false,
      selectedSegments: [],
      languages: [],
      loadedLanguages: false,
      translations: [],
      loadedTranslations: false,
      showAddTranslationPopup: false,
      showEditTranslationPopup: false,
      selectedTranslation: 0,
      removeTranslationInProgress: false,
      showTestPopup: false,
      activeTab: 0,
      emailsTabLoaded: false
    };
  },
  mounted () {
    this.loadItem();

    this.loadAdditionalData();
    this.loadTranslations();

    this.$bus.$on('mailing-translation-new-popup-close', this.closeAddTranslationPopup);
    this.$bus.$on('mailing-translation-edit-popup-close', this.closeEditTranslationPopup);
    this.$bus.$on('mailing-reload-translations', this.loadTranslations);
    this.$bus.$on('mailing-test-popup-close', this.closeTestPopup);
  },
  methods: {
    loadItem () {
      FormUtils.loadItemData(this, {
        method: 'get',
        endpointBase: '/marketing/mailings/item/',
        instanceFields: ['id', 'content', 'name', 'segments', 'subject', 'status', 'can_update', 'can_send'],
        loadedKey: 'loaded',
        successAction: (response) => {
          if (response.segments && Array.isArray(response.segments)) {
            this.segments = response.segments.map(segment => segment.id);
          }
        }
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/marketing/segments/items',
        loadedKey: 'loadedSegments',
        outputKey: 'segmentsList',
        errorKey: 'loadError',
      });

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/languages/items',
        loadedKey: 'loadedLanguages',
        outputKey: 'languages',
        errorKey: 'loadError'
      });
    },
    validateForm (stay = false) {
      FormUtils.validate(this.$refs['mailing-edit-form'], () => this.submitForm(stay));
    },
    submitForm (stay) {
      let formDataToSend = {
        content: this.content,
        name: this.name,
        segments: this.segments,
        subject: this.subject
      };

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/marketing/mailings/update/' + parseInt(this.$route.params.id, 10),
        formData: formDataToSend,
        successTitle: this.$t('marketing.contact.editMailingSuccessTitle'),
        successText: this.$t('marketing.contact.editMailingSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('marketing.contact.editMailingErrorText'),
        successAction: () => {
          if (stay) {
            this.loadItem();
            return
          }

          this.$router.push('/funkcje-sprzedazowe/wiadomosci-email');
        }
      });
    },
    getLanguageByID (languageID) {
      let filteredLanguages = this.languages.filter(language => language.id === languageID);

      if (filteredLanguages.length) {
        return filteredLanguages[0].name;
      }

      return '';
    },
    loadTranslations () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/marketing/mailings/translations/items',
        loadedKey: 'loadedTranslations',
        outputKey: 'translations',
        errorKey: 'loadError',
        params: {
          template_id: this.id
        }
      });
    },
    addTranslation () {
      this.showAddTranslationPopup = true;
    },
    editTranslation (translationData) {
      console.log(translationData)
      this.selectedTranslation = JSON.parse(JSON.stringify(translationData));
      this.showEditTranslationPopup = true;
    },
    closeAddTranslationPopup () {
      this.showAddTranslationPopup = false;
    },
    closeEditTranslationPopup () {
      this.showEditTranslationPopup = false;
    },
    removeTranslation (translationID) {
      this.removeTranslationInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/marketing/mailings/translations/delete/',
        id: translationID,
        successTitle: this.$t('settings.transaltion.deleteTranslationSuccessTitle'),
        successText: this.$t('settings.transaltion.deleteTranslationSuccessText'),
        confirmTitle: this.$t('settings.transaltion.deleteTranslationConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('settings.transaltion.deleteTranslationErrorText'),
        successAction: () => {
          this.removeTranslationInProgress = false;
          this.loadTranslations();
        },
        cancelAction: () => {
          this.removeTranslationInProgress = false;
        }
      });
    },
    stripHtml (html) {
      if (!html) {
        return '';
      }

      const cleanText = DOMPurify.sanitize(html, { ALLOWED_TAGS: [] });

      return cleanText.replace(/\s+/g, ' ').trim();
    },
    openTestPopup () {
      this.showTestPopup = true;
    },
    closeTestPopup () {
      this.showTestPopup = false;
    },
    sendMailing () {
      Vue.swal({
        title: this.$t('marketing.sendMailingConfirmTitle'),
        html: this.$t('marketing.sendMailingConfirmText'),
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('ui.yes'),
        cancelButtonText: this.$t('ui.cancel'),
        buttonsStyling: true
      }).then((result) => {
        if (result.value) {
          FormUtils.submit({
            $http: this.$http,
            endpoint: '/marketing/mailings/send/' + parseInt(this.$route.params.id, 10),
            formData: {},
            successTitle: this.$t('marketing.sendMailingSuccessTitle'),
            successText: this.$t('marketing.sendMailingSuccessText'),
            errorTitle: this.$t('ui.errorOccured'),
            errorText: this.$t('marketing.sendMailingErrorText'),
            successAction: () => {
              this.$router.push('/funkcje-sprzedazowe/wiadomosci-email');
            }
          });
        }
      });
    },
  },
  beforeDestroy () {
    this.$bus.$off('mailing-translation-new-popup-close', this.closeAddTranslationPopup);
    this.$bus.$off('mailing-translation-edit-popup-close', this.closeEditTranslationPopup);
    this.$bus.$off('mailing-reload-translations', this.loadTranslations);
    this.$bus.$off('mailing-test-popup-close', this.closeTestPopup);
  }
};
</script>

<style lang="scss" scoped>
form.is-loading {
  opacity: .25;
  pointer-events: none;
}
</style>
